import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row } from 'antd';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import api from 'helpers/api.helper';
import history from 'helpers/history.helper';
import { APP_URLS } from 'constants/url.constant';
import { setUser } from 'pages/auth/auth.slice';
import Routes from 'routes';
import FloatBarAccount from 'pages/counselor/account/FloatBarAccount';
import CodeVerifyErrorModal from 'pages/counselor/account/CodeVerifyErrorModal';
import { showNormal } from 'helpers/notification.helper';
import Loader from 'components/Loader';
import { allowedAdminPage } from 'helpers/role.helper';
import { isEmpty } from 'helpers/common.helper';

const mapState = state => ({
    account: state.auth.user,
});
const mapDispatch = { setUser };
const UpdateEmail = ({ account, setUser }) => {
    const inputRef = React.useRef(null);
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [loadingResend, setLoadingResend] = useState(false);
    const isAdmin = allowedAdminPage(account);

    const location = useLocation();
    const urlSearchParams = new URLSearchParams(location.search);
    const searchParamEmailSpace = urlSearchParams.get('email');
    const searchParamEmail = searchParamEmailSpace ? searchParamEmailSpace.replaceAll(' ', '+') : searchParamEmailSpace;
    const searchParamCode = urlSearchParams.get('code');

    useEffect(() => {
        if (isEmpty(account.tmpEmail) && isEmpty(searchParamEmail)) {
            history.push('/');
        }
    }, []);

    const [modalVisible, setModalVisible] = useState({
        verifyCode: false,
    });
    const sendEmail = async () => {
        setLoadingResend(true);
        await api.patch(
            APP_URLS.COUNSELOR_CHANGE_EMAIL_VERIFY_CODE.replace(':new_mail', account.tmpEmail || searchParamEmail),
            {
                email: account.tmpEmail || searchParamEmail,
            }
        );
    };

    const showVerifyCodeModal = () => {
        setLoading(false);
        setModalVisible({ verifyCode: true });
    };
    const closeModal = () => {
        setModalVisible({ verifyCode: false });
        inputRef.current.focus({
            cursor: 'all',
        });
    };

    const updateEmail = async values => {
        if (!account.tmpEmail && !searchParamEmail) {
            return;
        }
        setLoading(true);
        values.email = account.tmpEmail || searchParamEmail;
        try {
            const response = await api.patch(APP_URLS.COUNSELOR_UPDATE_EMAIL, values, {}, {}, true);
            if (response) {
                setLoading(false);
                showNormal('', t('message:Saved your changes'), 5);
                setUser(response);
                history.push(isAdmin ? Routes.private.ADMIN_PROFILE.path : Routes.private.COUNSELOR_ACCOUNT.path);
            } else {
                showVerifyCodeModal();
            }
        } catch (e) {
            showVerifyCodeModal();
        }
    };

    let initialValues = Object.assign({}, { ...account, verify_code: searchParamCode });

    const sharedProps = {
        ref: inputRef,
    };

    return (
        <div className="px-48">
            <h1 className="page-title">{'認証コードの入力'}</h1>

            <Form
                layout="vertical"
                form={form}
                initialValues={initialValues}
                onFinish={async values => {
                    await updateEmail(values);
                }}
                requiredMark={false}
            >
                <p className="fw-b fs-16 mb-4 mt-32">メールアドレス</p>
                <label className="fs-20">{account.tmpEmail || searchParamEmail}</label>
                <p className="fs-16 mb-32 mt-32">
                    認証コードが上記のメールアドレスに送信されました。
                    <br />
                    メールに記載されている認証コードを入力し、「変更」を押すと変更が完了します。
                </p>

                <Row className="" align="middle">
                    <Col className="mr-16">
                        <Form.Item
                            name="verify_code"
                            label={t('field:Authentication Code')}
                            validateTrigger={false}
                            rules={[
                                {
                                    required: true,
                                    message: t('validation:Please enter {{field}}', {
                                        field: t('field: Authentication Code'),
                                    }),
                                },
                            ]}
                        >
                            <Input autoFocus style={{ maxWidth: 280 }} {...sharedProps} />
                        </Form.Item>
                    </Col>
                </Row>

                <p className="mt-48">
                    認証コードを再送したい場合は
                    <Button
                        type="link"
                        disabled={loadingResend}
                        onClick={() => {
                            sendEmail().then(() => {
                                setLoadingResend(false);
                                showNormal('', t('message:We sent an email enclosed authentication code'), 5);
                                inputRef.current.focus({
                                    cursor: 'all',
                                });
                            });
                        }}
                        className="p-0 td-link-bolder"
                    >
                        {'こちら'}
                        {loadingResend ? <Loader className={'resend-verify-code-spin'} /> : <></>}
                    </Button>
                </p>

                <FloatBarAccount>
                    <Button
                        type=""
                        className="fw-b btn-float-bar"
                        onClick={() => {
                            if (isAdmin) {
                                history.push(Routes.private.ADMIN_PROFILE.path);
                            } else {
                                history.push(Routes.private.COUNSELOR_ACCOUNT.path);
                            }
                        }}
                    >
                        {'キャンセル'}
                    </Button>
                    <Button type="primary" loading={loading} className="fw-b btn-float-bar" htmlType="submit">
                        {'変更'}
                    </Button>
                    <CodeVerifyErrorModal visible={modalVisible.verifyCode} onClose={() => closeModal()} />
                </FloatBarAccount>
            </Form>
        </div>
    );
};

export default connect(mapState, mapDispatch)(UpdateEmail);

UpdateEmail.propTypes = {
    account: PropTypes.any,
    setUser: PropTypes.func,
};
